/*
* Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
*/
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import useJobIdCopyToClipboard from '@ion/applications/src/components/jobs/useJobIdCopyToClipboard';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJob} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import CopyButton from '@ion/dls/src/components/buttons/CopyButton';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import JobStatusCell from './cells/JobStatusCell';
import WatchJobBell, {useWatchBellVisible} from './cells/WatchJobBell';

export type JobSummaryProps = {
  job: DownloadJob;
}

const getDevicesCount = (tasks) => (tasks?.[0]?.counts?.total);

export const useStyles = makeStyles(() => {
  return {
    jobContainer : {
      display         : 'flex',
      alignItems      : 'stretch',
      justifyContent  : 'space-between',
      borderBottom    : `1px solid ${dlsColor('grey5')}`,
      padding         : '10px 0px 10px 10px',
      fontFamily      : 'Arial, sans-serif',
      backgroundColor : dlsColor('white'),
      weight          : '98%',
      overflowX       : 'auto',
      overflowY       : 'hidden',
      minHeight       : 'fit-content'
    },
    jobDetail : {
      display       : 'flex',
      height        : 'auto',
      minHeight     : 40,
      maxHeight     : 136,
      padding       : '0px 10px',
      borderLeft    : `4px solid ${dlsColor('paleBlue')}`,
      flexDirection : 'column',
      alignItems    : 'flex-start'
    },
    label : {
      color         : dlsColor('grey3'),
      fontWeight    : 500,
      fontSize      : '10px',
      textTransform : 'uppercase',
      letterSpacing : '1px',
      marginBottom  : '4px'
    },
    value : {
      fontSize   : '16px',
      color      : dlsColor('grey1'),
      fontWeight : 500,
      padding    : '5px 0px',
      fontFamily : 'Roboto',
      lineHeight : '20px'
    },
    jobIdCell : {
      display    : 'flex',
      alignItems : 'center',
      gap        : '5px'
    },
    multiLineText : {
      whiteSpace   : 'normal',
      overflowWrap : 'anywhere',
      wordBreak    : 'break-word'
    },
    jobStatusCell : {
      display    : 'flex',
      alignItems : 'baseline',
      marginTop  : 8
    },
    labelMinWidth : {
      minWidth : 82
    },
    labelDescripMinWidth : {
      minWidth : 153
    }
  };
});

/**
* The component that displays the job details of selected job id in summary cards.
*/
export const JobSummary = ({job}: JobSummaryProps) => {
  const classes               = useStyles();
  const copyValuesToClipboard = useJobIdCopyToClipboard();
  const bellVisible           = useWatchBellVisible(job?.jobId, job?.progressStatus);

  return (
    <div className={classes.jobContainer}>
      <div className={classes.jobDetail}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.jobId')}</span>
        <div className={classes.jobIdCell}>
          <span className={classes.value}>{job?.jobId}</span>
          <Render.If condition={job?.jobId}>
            <div style={{paddingBottom: 4}}>
              <CopyButton size={IconSize.smallMedium} onCopy={function() { copyValuesToClipboard(job?.jobId); }} />
            </div>
          </Render.If>
        </div>
      </div>
      <Render.If condition={bellVisible}>
        <div className={classes.jobDetail}>
          <span className={classes.label}>{translate('jobsearchengine:jobDetails.notifications')}</span>
          <div className={classes.jobIdCell}>
            <WatchJobBell jobId={job?.jobId} status={job?.progressStatus} />
          </div>
        </div>
      </Render.If>
      <div className={classes.jobDetail}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.jobCreator')}</span>
        <span className={classnames(classes.value, classes.multiLineText, classes.labelMinWidth)}>{job?.username}</span>
      </div>
      <div className={classnames(classes.jobDetail, classes.labelDescripMinWidth)}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.description')}</span>
        <span className={classnames(classes.value, classes.multiLineText)}>{job?.description}</span>
      </div>
      <div className={classes.jobDetail}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.tasks')}</span>
        <span className={classes.value}>{job?.tasks?.length}</span>
      </div>
      <div className={classes.jobDetail}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.devices')}</span>
        <span className={classes.value}>{getDevicesCount(job?.tasks)}</span>
      </div>
      <div className={classes.jobDetail}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.account')}</span>
        <span className={classes.value}>{job?.account?.title}</span>
      </div>
      <div className={classes.jobDetail}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.status')}</span>
        <div className={classes.jobStatusCell}>
          <JobStatusCell key={`job-status-${job?.id}`} status={job?.progressStatus} progress={job?.progress}/>
        </div>
      </div>
      <div className={classes.jobDetail}>
        <span className={classes.label}>{translate('jobsearchengine:jobDetails.createdOn')}</span>
        <span className={classnames(classes.value, classes.labelMinWidth)}>{formatDateTime(job?.createdOn)}</span>
      </div>
    </div>
  );
};

export default JobSummary;
